import "./Summary.scss";

import clsx from "clsx";
import { FC, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { CalendarBlank, ConnectionIcon, SpeedIcon } from "@/assets/icons/icons";
import { SectionButtonHeader } from "@/components/Interface/SectionButtonHeader/SectionButtonHeader";
import {
  useGetFlatPricesAndName,
  useIsMobileAbo,
} from "@/hooks/useDataContextHooks";
import { useWizardBasePath } from "@/hooks/useWizard";
import { DataContext } from "@/provider/DataContextProvider";
import { ServiceIDs } from "@/utils/constants";
import { getEntry } from "@/utils/dataContextHelpers";
import { getDeviceDescription, TariffTypes } from "@/utils/deviceUtils";
import { getIncludedFeaturesRateSelection } from "@/utils/tariffUtils";

interface Props {
  isFlatHome?: boolean;
}

/**
 * This component should implement the device assignment
 * and the rate picker during the onboarding.
 */
export const Summary: FC<Props> = ({ isFlatHome }) => {
  const { dataContext } = useContext(DataContext);
  const basePath = useWizardBasePath();
  const navigate = useNavigate();
  const { t } = useTranslation("translation", {
    keyPrefix: "Onboarding.sections.checkout.summary",
  });
  const { t: tDefault } = useTranslation("translation");

  const entry = getEntry(dataContext);
  const deviceType = getDeviceDescription(entry?.device?.description);
  const customData = entry?.customData;
  const deviceName = customData?.["SimAlias"] as string;
  const { flatName } = useGetFlatPricesAndName();
  const isMobileAbo = useIsMobileAbo();
  const hasONP = entry?.onp;
  const onpTranslation = hasONP
    ? t("numberPortingIncluded")
    : t("numberPortingNotIncluded");

  // in case of flatHome we display additional section using this data
  const flatHomeFeatures = isFlatHome
    ? getIncludedFeaturesRateSelection(
        t,
        entry?.options?.[0]?.product?.id === ServiceIDs.FlatHome4G
          ? TariffTypes.flatHome4G
          : TariffTypes.flatHome5G,
        365, // duration 1 year
      )
    : [];
  const iconStyles = "size-6 flex-shrink-0 text-primary-100";
  const flatHomeIcons = isFlatHome
    ? [
        <ConnectionIcon key="connection" className={iconStyles} />,
        <SpeedIcon key="speed" className={iconStyles} />,
        <CalendarBlank key="calendar" className={iconStyles} />,
      ]
    : [];

  return (
    <div className="payment-summary">
      <p className="summary-title">{t("title")}</p>
      <div className="selected-summary">
        <SectionButtonHeader header={t("assignedDevice")}>
          <div className="selection-row">
            {isFlatHome || (
              <button
                className="change-button"
                onClick={() => navigate(`${basePath}/2`)}
              >
                {t("changeRate")}
              </button>
            )}
          </div>
        </SectionButtonHeader>
        <div className="spans">
          <span>
            {isFlatHome ? tDefault("Common.devices.Router") : deviceType}
          </span>
          <span>|</span>
          <span>{deviceName}</span>
        </div>
      </div>
      <div className="selected-summary">
        <div className="selection-row">
          <p className="header">{t("rateOptions")}</p>
          {isFlatHome || (
            <button
              className="change-button"
              onClick={() => navigate(`${basePath}/3`)}
            >
              {t("changeRate")}
            </button>
          )}
        </div>
        <div className="spans w-60 xsPlus:w-full">
          <span>{flatName}</span>
          {isMobileAbo && !isFlatHome && (
            <>
              <span>|</span>
              <span className={clsx(!hasONP && "!text-secondary-100")}>
                {onpTranslation}
              </span>
            </>
          )}
        </div>
        {isFlatHome && (
          <div className="mt-2 border-l-2 border-primary-100 pl-6 text-base font-semibold">
            {flatHomeFeatures.map((feature, index) => (
              <p
                key={feature.name}
                className="mb-2 flex flex-row gap-2 font-normal"
              >
                {flatHomeIcons[index]}
                {feature.name}
              </p>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
